import { ChainData, Token } from "@0xsquid/squid-types";
import {
  Address,
  nativeToScVal,
  Networks as StellarNetwork,
  StrKey,
} from "@stellar/stellar-sdk";
import { CHAIN_IDS } from "../../core/constants";
import {
  StellarChain,
  StellarHorizonAssetType,
  StellarHorizonIssuedAsset,
  StellarHorizonNativeAsset,
  StellarIssuedToken,
  StellarToken,
  StellarTokenType,
} from "../../core/types/stellar";
import { TrustLineAsset } from "../../core/types/tokens";

export function isStellarAddressValid(address: string): boolean {
  return StrKey.isValidEd25519PublicKey(address);
}

export function getStellarNetwork(chainId: string): StellarNetwork | null {
  switch (chainId) {
    case CHAIN_IDS.STELLAR:
      return StellarNetwork.PUBLIC;
    case CHAIN_IDS.STELLAR_TESTNET:
      return StellarNetwork.TESTNET;
    default:
      return null;
  }
}

/**
 * Converts a Stellar address to a Soroban Contract address value
 */
export function stellarAddressToScVal(addressString: string) {
  return nativeToScVal(Address.fromString(addressString), {
    type: "address",
  });
}

export function getStellarTrustLineAsset(
  token: StellarIssuedToken
): TrustLineAsset | null {
  // The address format for issued assets is `CODE-ISSUER`
  // Example: USDC-GA5ZSEJYB37JRC5AVCIA5MOP4RHTM335X2KGX3IHOJAPP5RE34K4KZVN
  const [code, issuer] = token.address.split("-");

  if (!code || !issuer) {
    return null;
  }

  return {
    code,
    issuer,
  };
}

export function isStellarToken(token: Token): token is StellarToken {
  try {
    const stellarToken = token as StellarToken;

    return (
      Object.values(StellarTokenType).includes(
        stellarToken.chainAssetConfig.stellar.assetType
      ) &&
      typeof stellarToken.chainAssetConfig.stellar.contractAddress === "string"
    );
  } catch {
    return false;
  }
}

export function isStellarIssuedToken(
  token: Token
): token is StellarIssuedToken {
  try {
    return (
      isStellarToken(token) &&
      token.chainAssetConfig.stellar.assetType === StellarTokenType.ISSUER_TOKEN
    );
  } catch {
    return false;
  }
}

export function getStellarHorizonApiUrl(chain: ChainData): string | null {
  try {
    const stellarChain = chain as StellarChain;

    const [horizonApiUrl] = stellarChain.horizonRpcList;

    if (typeof horizonApiUrl !== "string") {
      throw new Error("Invalid Horizon API URL");
    }

    return horizonApiUrl;
  } catch {
    return null;
  }
}

const VALID_ASSET_TYPES = new Set<string>(
  Object.values(StellarHorizonAssetType)
);

function isValidNativeAsset(
  asset: unknown
): asset is StellarHorizonNativeAsset {
  return (
    typeof asset === "object" &&
    asset !== null &&
    "balance" in asset &&
    typeof asset.balance === "string" &&
    "asset_type" in asset &&
    asset.asset_type === StellarHorizonAssetType.NATIVE
  );
}

export function isValidIssuedAsset(
  asset: unknown
): asset is StellarHorizonIssuedAsset {
  return (
    typeof asset === "object" &&
    asset !== null &&
    "balance" in asset &&
    typeof asset.balance === "string" &&
    "asset_type" in asset &&
    typeof asset.asset_type === "string" &&
    VALID_ASSET_TYPES.has(asset.asset_type) &&
    asset.asset_type !== StellarHorizonAssetType.NATIVE &&
    "asset_code" in asset &&
    typeof asset.asset_code === "string" &&
    "asset_issuer" in asset &&
    typeof asset.asset_issuer === "string" &&
    "limit" in asset &&
    typeof asset.limit === "string"
  );
}

export function isValidHorizonAsset(
  asset: unknown
): asset is StellarHorizonNativeAsset | StellarHorizonIssuedAsset {
  return isValidNativeAsset(asset) || isValidIssuedAsset(asset);
}
